import {
  BoldOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import Image from "@tiptap/extension-image";
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Button, Divider, Input, Modal, Space, Tooltip, Typography, message } from "antd";
import { useEffect, useRef, useState } from "react";

import { uploadBlogImage } from "./blog-image";

// The post body editor (docs/MVP.md → Blog): headings 2 and 3 (the title is the h1), lists,
// quotes, links and images with alt text. apps/api sanitizes what it sends.

const STYLES = `
.blog-editor .tiptap { min-height: 420px; padding: 16px; outline: none; font-size: 16px; line-height: 1.7; }
.blog-editor .tiptap h2 { font-size: 24px; font-weight: 600; margin: 24px 0 8px; }
.blog-editor .tiptap h3 { font-size: 20px; font-weight: 600; margin: 20px 0 8px; }
.blog-editor .tiptap p { margin: 0 0 12px; }
.blog-editor .tiptap ul, .blog-editor .tiptap ol { padding-inline-start: 24px; margin: 0 0 12px; }
.blog-editor .tiptap blockquote { border-inline-start: 3px solid #d9d9d9; margin: 0 0 12px; padding-inline-start: 12px; color: #595959; }
.blog-editor .tiptap img { max-width: 100%; height: auto; border-radius: 8px; }
.blog-editor .tiptap img.ProseMirror-selectednode { outline: 2px solid #1677ff; }
.blog-editor .tiptap a { color: #1677ff; text-decoration: underline; }
`;

function ToolbarButton(props: {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={props.title}>
      <Button
        size="small"
        type={props.active ? "primary" : "text"}
        disabled={props.disabled}
        onClick={props.onClick}
        aria-label={props.title}
        aria-pressed={props.active}
      >
        {props.children}
      </Button>
    </Tooltip>
  );
}

function Toolbar({ editor, onImage }: { editor: Editor; onImage: () => void }) {
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      h2: current.isActive("heading", { level: 2 }),
      h3: current.isActive("heading", { level: 3 }),
      bold: current.isActive("bold"),
      italic: current.isActive("italic"),
      underline: current.isActive("underline"),
      bullet: current.isActive("bulletList"),
      ordered: current.isActive("orderedList"),
      quote: current.isActive("blockquote"),
      link: current.isActive("link"),
      canUndo: current.can().undo(),
      canRedo: current.can().redo(),
    }),
  });
  const [linkOpen, setLinkOpen] = useState(false);
  const [href, setHref] = useState("");
  const chain = () => editor.chain().focus();

  function openLink() {
    setHref((editor.getAttributes("link")["href"] as string | undefined) ?? "");
    setLinkOpen(true);
  }

  function saveLink() {
    const value = href.trim();
    if (!value) chain().extendMarkRange("link").unsetLink().run();
    else chain().extendMarkRange("link").setLink({ href: value }).run();
    setLinkOpen(false);
  }

  return (
    <>
      <Space wrap size={4} style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
        <ToolbarButton
          title="Ara başlık (Başlık 2)"
          active={state.h2}
          onClick={() => chain().toggleHeading({ level: 2 }).run()}
        >
          B2
        </ToolbarButton>
        <ToolbarButton
          title="Alt başlık (Başlık 3)"
          active={state.h3}
          onClick={() => chain().toggleHeading({ level: 3 }).run()}
        >
          B3
        </ToolbarButton>
        <Divider type="vertical" />
        <ToolbarButton title="Kalın" active={state.bold} onClick={() => chain().toggleBold().run()}>
          <BoldOutlined />
        </ToolbarButton>
        <ToolbarButton
          title="İtalik"
          active={state.italic}
          onClick={() => chain().toggleItalic().run()}
        >
          <ItalicOutlined />
        </ToolbarButton>
        <ToolbarButton
          title="Altı çizili"
          active={state.underline}
          onClick={() => chain().toggleUnderline().run()}
        >
          <UnderlineOutlined />
        </ToolbarButton>
        <Divider type="vertical" />
        <ToolbarButton
          title="Madde listesi"
          active={state.bullet}
          onClick={() => chain().toggleBulletList().run()}
        >
          <UnorderedListOutlined />
        </ToolbarButton>
        <ToolbarButton
          title="Numaralı liste"
          active={state.ordered}
          onClick={() => chain().toggleOrderedList().run()}
        >
          <OrderedListOutlined />
        </ToolbarButton>
        <ToolbarButton
          title="Alıntı"
          active={state.quote}
          onClick={() => chain().toggleBlockquote().run()}
        >
          ”
        </ToolbarButton>
        <Divider type="vertical" />
        <ToolbarButton title="Bağlantı" active={state.link} onClick={openLink}>
          <LinkOutlined />
        </ToolbarButton>
        <ToolbarButton title="Görsel ekle" onClick={onImage}>
          <PictureOutlined />
        </ToolbarButton>
        <Divider type="vertical" />
        <ToolbarButton
          title="Geri al"
          disabled={!state.canUndo}
          onClick={() => chain().undo().run()}
        >
          <UndoOutlined />
        </ToolbarButton>
        <ToolbarButton
          title="Yinele"
          disabled={!state.canRedo}
          onClick={() => chain().redo().run()}
        >
          <RedoOutlined />
        </ToolbarButton>
      </Space>
      <Modal
        title="Bağlantı"
        open={linkOpen}
        okText="Kaydet"
        cancelText="Vazgeç"
        onOk={saveLink}
        onCancel={() => setLinkOpen(false)}
        destroyOnHidden
      >
        <Input
          autoFocus
          aria-label="Bağlantı adresi"
          placeholder="https://hedefuni.com/plans"
          value={href}
          onChange={(event) => setHref(event.target.value)}
          onPressEnter={saveLink}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          Sitedeki sayfalara verilen bağlantılar iç bağlantı sayılır. Boş bırakırsan bağlantı
          kaldırılır.
        </Typography.Paragraph>
      </Modal>
    </>
  );
}

/** Uncontrolled after the first render: `value` sets the initial content only. */
export function BlogEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ url: string } | null>(null);
  const [alt, setAlt] = useState("");
  const [uploading, setUploading] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: { openOnClick: false, autolink: true },
        code: false,
        codeBlock: false,
      }),
      Image,
    ],
    content: value,
    onUpdate: ({ editor: current }) => onChange(current.getHTML()),
  });

  // The loaded post arrives after the first render.
  const loaded = useRef(false);
  useEffect(() => {
    if (!editor || loaded.current || !value) return;
    loaded.current = true;
    if (editor.getHTML() !== value) editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadBlogImage(file);
      setAlt("");
      setPending({ url: uploaded.url });
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Görsel yüklenemedi.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  function insertImage() {
    if (!editor || !pending || !alt.trim()) return;
    editor.chain().focus().setImage({ src: pending.url, alt: alt.trim() }).run();
    setPending(null);
  }

  if (!editor) return null;
  return (
    <div className="blog-editor" style={{ border: "1px solid #d9d9d9", borderRadius: 8 }}>
      <style>{STYLES}</style>
      <Toolbar editor={editor} onImage={() => fileInput.current?.click()} />
      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(event) => onFile(event.target.files?.[0])}
      />
      {uploading ? (
        <Typography.Text type="secondary" style={{ padding: "0 16px" }}>
          Görsel yükleniyor…
        </Typography.Text>
      ) : null}
      <EditorContent editor={editor} />
      <Modal
        title="Görselin açıklaması"
        open={pending !== null}
        okText="Ekle"
        cancelText="Vazgeç"
        okButtonProps={{ disabled: !alt.trim() }}
        onOk={insertImage}
        onCancel={() => setPending(null)}
        destroyOnHidden
      >
        {pending ? (
          <img
            src={pending.url}
            alt=""
            style={{ maxWidth: "100%", borderRadius: 8, marginBottom: 12 }}
          />
        ) : null}
        <Input
          autoFocus
          aria-label="Görselin açıklaması (alt metin)"
          placeholder="Ör. TYT matematik konu dağılımı grafiği"
          value={alt}
          maxLength={200}
          onChange={(event) => setAlt(event.target.value)}
          onPressEnter={insertImage}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          Görselde ne olduğunu kısaca yaz; Google ve ekran okuyucular bunu kullanır.
        </Typography.Paragraph>
      </Modal>
    </div>
  );
}
